import { z } from 'zod';
import { businessSchema } from '../../common/app-registry.schema';
import { languageSchema } from '../../common/language.schema';
import { memberTypeSchema } from '../../common/member.schema';
import { partyRoleSchema } from '../../common/party.schema';
import { dateSchema, dateTypeSchema, idSchema, nameSchema, objectIdSchema } from '../../validation';
import { funnelStepSchema } from '../lead/lead.funnel.model';
import { leadStatusSchema } from '../lead/lead.status.model';
import { orgStatusSchema } from '../org/org.enums';

/**
 * Outbound pipes a message may use. Mirrors the backend catalog's
 * `MessageChannel`; lives here so a campaign document and a targeting
 * query can name channels without importing backend code.
 */
export const outboundChannelSchema = z.enum(['whatsapp', 'push', 'email']);
export type OutboundChannel = z.infer<typeof outboundChannelSchema>;

// ─── Sources ─────────────────────────────────────────────────────────

/** Leads — the CRM: prospects and everyone who ever wrote to a Hero line. */
export const leadTargetFiltersSchema = z.object({
  /** Which brand's funnel to read (`Lead.funnels[].business`). */
  business: businessSchema.optional(),
  /** Leads that completed ALL of these steps. */
  funnelStepCompleted: z.array(funnelStepSchema).optional(),
  /** Leads that completed NONE of these steps. */
  funnelStepNotCompleted: z.array(funnelStepSchema).optional(),
  leadStatusIn: z.array(leadStatusSchema).optional(),
  /** Funnel `first_order` / `first_waybill`. */
  hasOrders: z.boolean().optional(),
  /** `Lead.converted` or funnel `account_created`. */
  hasAccount: z.boolean().optional(),
  /** `Lead.UTags[].code`. */
  tags: z.array(idSchema).optional(),
  /** Hand-picked leads (Leads list multi-select). */
  leadIds: z.array(objectIdSchema).optional(),
});
export type LeadTargetFilters = z.infer<typeof leadTargetFiltersSchema>;

/**
 * Who inside a targeted org receives the message.
 *
 * Default is **admins only** — the people who run the account. Every
 * widening is explicit: more member types, or the `Driver` rows the org
 * created for people without the app (reachable on WhatsApp only).
 */
export const orgRecipientScopeSchema = z.object({
  memberTypes: z.array(memberTypeSchema).min(1).default(['admin']),
  includeDrivers: z.boolean().default(false),
});
export type OrgRecipientScope = z.infer<typeof orgRecipientScopeSchema>;

/** Orgs — carriers, brokers, business customers, by network role. */
export const orgTargetFiltersSchema = z.object({
  orgIds: z.array(objectIdSchema).optional(),
  /** Any of these network roles on `Org.roles[]` (carrier_company, broker, business_customer …). */
  roles: z.array(partyRoleSchema).optional(),
  status: z.array(orgStatusSchema).optional(),
  createdDateType: dateTypeSchema.optional(),
});
export type OrgTargetFilters = z.infer<typeof orgTargetFiltersSchema>;

/** Users — accounts, directly. */
export const userTargetFiltersSchema = z.object({
  userIds: z.array(objectIdSchema).optional(),
  phones: z.array(idSchema).optional(),
  /** Any of these network roles on `User.roles[]`. */
  roles: z.array(partyRoleSchema).optional(),
  status: z.array(z.enum(['active', 'pending', 'inactive', 'blocked'])).optional(),
  createdDateType: dateTypeSchema.optional(),
});
export type UserTargetFilters = z.infer<typeof userTargetFiltersSchema>;

/** Most rows one uploaded contacts sheet may carry — mirrors the campaign run cap. */
export const TARGET_CONTACTS_MAX_ROWS = 500;

/** One row of an uploaded contacts sheet (Excel template columns). */
export const targetContactRowSchema = z.object({
  phone: idSchema,
  email: idSchema.optional(),
  name: nameSchema.optional(),
  companyName: nameSchema.optional(),
  city: nameSchema.optional(),
  language: languageSchema.optional(),
});
export type TargetContactRow = z.infer<typeof targetContactRowSchema>;

/**
 * Who a past campaign's recipient became, for follow-ups.
 *
 * - `not_replied` — reached on WhatsApp, no inbound reply yet.
 * - `read`        — read, no reply yet.
 * - `delivered`   — delivered, not read, no reply yet.
 * - `replied`     — answered on the thread after the send.
 * - `failed`      — every requested channel failed.
 */
export const campaignEngagementFilterSchema = z.enum([
  'not_replied',
  'read',
  'delivered',
  'replied',
  'failed',
]);
export type CampaignEngagementFilter = z.infer<typeof campaignEngagementFilterSchema>;

export const targetSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('leads'), filters: leadTargetFiltersSchema.default({}) }),
  z.object({
    kind: z.literal('orgs'),
    filters: orgTargetFiltersSchema.default({}),
    recipients: orgRecipientScopeSchema.default({ memberTypes: ['admin'], includeDrivers: false }),
  }),
  z.object({ kind: z.literal('users'), filters: userTargetFiltersSchema.default({}) }),
  /** Every active account in the system (+ optionally every Driver row). */
  z.object({
    kind: z.literal('everyone'),
    includeDrivers: z.boolean().default(false),
  }),
  /**
   * An uploaded contacts sheet. Each phone is matched to an existing Lead,
   * else an existing User, else it is new. Resolving never writes; the
   * campaign send creates the missing leads when `createMissingLeads`.
   */
  z.object({
    kind: z.literal('contacts'),
    rows: z.array(targetContactRowSchema).min(1).max(TARGET_CONTACTS_MAX_ROWS),
    createMissingLeads: z.boolean().default(true),
  }),
  /** Recipients of a past campaign, by how they engaged — follow-ups. */
  z.object({
    kind: z.literal('campaign'),
    campaignId: objectIdSchema,
    engagementIn: z.array(campaignEngagementFilterSchema).min(1),
  }),
]);
export type TargetSource = z.infer<typeof targetSourceSchema>;

/** Filters applied to the union of every source, per person. */
export const targetCommonFiltersSchema = z.object({
  /** The person's follow-up / thread language. */
  language: languageSchema.optional(),
  /** "Talked to us" window on the phone's newest WhatsApp thread (`lastInboundAt`). */
  lastInboundDateType: dateTypeSchema.optional(),
  /** "Gone quiet since" — last inbound strictly before this instant. */
  lastInboundBefore: dateSchema.optional(),
  /** Only threads on this Hero-owned line count for the inbound window / human-mode check. */
  channelAccountId: objectIdSchema.optional(),
  excludePhones: z.array(idSchema).optional(),
  /** Skip anyone who received the same template inside this many days (WhatsApp). Default 0 (allow resends); set e.g. 7 for open blasts. */
  dedupeWindowDays: z.number().int().min(0).max(90).optional(),
});
export type TargetCommonFilters = z.infer<typeof targetCommonFiltersSchema>;

/**
 * A targeting query: **who**. Sources are unioned; filters inside a
 * source AND together; `common` applies to the union. The server adds
 * its own exclusions on top (opt-out, DND, dedupe, human-held threads,
 * unreachable on the requested channels) — those are not switches.
 *
 * One shape for campaigns, the agent, bulk waybills, or anything else
 * that needs "these people". See `docs/ai/human-takeover.md` § 8.
 */
export const targetQuerySchema = z.object({
  sources: z.array(targetSourceSchema).min(1),
  common: targetCommonFiltersSchema.default({}),
});
export type TargetQuery = z.infer<typeof targetQuerySchema>;

// ─── Resolved rows ───────────────────────────────────────────────────

/** Why a resolved person is not sent to. */
export const targetSkipReasonSchema = z.enum([
  'opted_out',
  'dnd',
  'dedupe_window',
  'human_mode',
  'no_phone',
  'invalid_phone',
  'no_push_user',
  'unreachable',
]);
export type TargetSkipReason = z.infer<typeof targetSkipReasonSchema>;

/** Which source produced the row (the first one, when several matched). */
export const targetOriginSchema = z.enum(['lead', 'org_member', 'org_driver', 'user', 'contact']);
export type TargetOrigin = z.infer<typeof targetOriginSchema>;

/**
 * What an uploaded contact row matched: an existing Lead, else an
 * existing User (no lead yet), else nobody (`new`).
 */
export const targetMatchSchema = z.enum(['lead', 'user', 'new']);
export type TargetMatch = z.infer<typeof targetMatchSchema>;

/**
 * One person, however they were found. Deduped per person: someone who
 * is both a lead and an org admin is one row with both ids filled.
 *
 * `reach` says which channels can actually deliver to them; `skip`
 * (when set) says why none of the requested ones can.
 */
export const targetPersonSchema = z.object({
  /** Dedupe key — the phone when there is one, else `user:<id>`. */
  key: idSchema,
  phone: idSchema.optional(),
  email: idSchema.optional(),
  /** Platform account — what push needs. */
  userId: objectIdSchema.optional(),
  personName: nameSchema.optional(),
  language: languageSchema.optional(),
  orgId: objectIdSchema.optional(),
  currentOrgName: nameSchema.optional(),
  orgRoles: z.array(partyRoleSchema).optional(),
  memberType: memberTypeSchema.optional(),
  leadId: objectIdSchema.optional(),
  leadStatus: leadStatusSchema.optional(),
  driverId: objectIdSchema.optional(),
  conversationId: objectIdSchema.optional(),
  lastInboundAt: dateSchema.optional(),
  origin: targetOriginSchema,
  /** Uploaded contacts only — who the phone matched. */
  match: targetMatchSchema.optional(),
  /** Uploaded contacts only — fills blanks on a lead the send creates. */
  companyName: nameSchema.optional(),
  city: nameSchema.optional(),
  reach: z.object({
    whatsapp: z.boolean(),
    push: z.boolean(),
    email: z.boolean(),
  }),
  skip: targetSkipReasonSchema.optional(),
});
export type TargetPerson = z.infer<typeof targetPersonSchema>;
